// Drawer — a side panel for record detail without losing the list.
//
// The console's answer to "click a row to see everything about it". A drawer
// beats a route change for triage work: the list stays on screen, so the
// operator keeps their filters, their scroll position and their place in a
// queue of records they are working through.
//
// Detail pages still exist for deep links and for records that deserve a full
// page (a subscription with its whole invoice ledger). The rule of thumb:
// drawer for inspect-and-move-on, page for work-here-a-while.
//
// Radix Dialog underneath, so focus trap / Escape / scroll lock are correct.

import { cn } from '@fajrarabi/ui';
import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { Icon } from './Icon';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Status badge or id, shown beside the title. */
  badge?: ReactNode;
  children: ReactNode;
  /** Sticky action bar pinned to the bottom. */
  footer?: ReactNode;
  width?: 'md' | 'lg' | 'xl';
}

const WIDTH: Record<NonNullable<DrawerProps['width']>, string> = {
  md: 'w-[min(28rem,100vw)]',
  lg: 'w-[min(36rem,100vw)]',
  xl: 'w-[min(48rem,100vw)]',
};

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  badge,
  children,
  footer,
  width = 'lg',
}: DrawerProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-admin-900/30 backdrop-blur-[2px]" />
        <RadixDialog.Content
          className={cn(
            // Anchored to the INLINE end, so it opens from the right in LTR and
            // from the left in RTL without a second rule.
            'fixed inset-y-0 end-0 z-50 flex flex-col border-s border-admin-200 bg-white shadow-2xl focus:outline-none',
            WIDTH[width],
          )}
        >
          <div className="flex items-start gap-3 border-b border-admin-200 px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <RadixDialog.Title className="truncate text-base font-semibold text-admin-900">
                  {title}
                </RadixDialog.Title>
                {badge}
              </div>
              {description && (
                <RadixDialog.Description asChild>
                  <div className="mt-0.5 text-sm text-admin-500">{description}</div>
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close
              aria-label="Close panel"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-admin-500 transition-colors hover:bg-admin-100 hover:text-admin-700"
            >
              <Icon name="close" className="h-4 w-4" />
            </RadixDialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-admin-200 bg-admin-50 px-5 py-3">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Definition list row — the workhorse of drawer and detail content.
 * Label in a fixed inline-start column, value filling the rest.
 */
export function DetailRow({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-baseline gap-x-4 gap-y-0.5 py-2', className)}>
      <dt className="w-32 shrink-0 text-xs font-medium uppercase tracking-wide text-admin-400">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-sm text-admin-800">{children}</dd>
    </div>
  );
}

/** Grouped detail rows with an optional section heading. */
export function DetailSection({
  title,
  children,
  actions,
  className,
}: {
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('py-2', className)}>
      {(title || actions) && (
        <div className="mb-1 flex items-center justify-between gap-2">
          {title && (
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-admin-500">
              {title}
            </h3>
          )}
          {actions}
        </div>
      )}
      <dl className="divide-y divide-admin-100">{children}</dl>
    </section>
  );
}
